/**
 * PeerContext — "You save more than 73% of students your age."
 *
 * Shows percentile rank across 4 metrics (net worth, income,
 * savings, savings rate) compared to Canadian age-group benchmarks.
 * Each metric has a visual percentile bar and above/below median badge.
 *
 * Self-contained — reads all data from useApp() and peerEngine.
 */
import { useMemo } from "react";
import { Card } from '../../components/Card';
import { Heading } from '../../components/Heading';
import { useApp } from '../../hooks/useApp';
import { calculatePeerComparison, getPeerMessage } from './peerEngine';

export default function PeerContext() {
  const { s, c, proj } = useApp();

  const data = useMemo(
    () => calculatePeerComparison(s, proj),
    [s, proj]
  );

  const message = getPeerMessage(data);
  const { comparisons, overallPercentile, bracket } = data;

  // Color based on overall percentile
  const overallColor =
    overallPercentile >= 75 ? c.green :
    overallPercentile >= 50 ? c.blue :
    overallPercentile >= 35 ? c.orange :
    c.muted;

  return (
    <Card c={c} delay={0.28}>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 20, flexWrap: "wrap", gap: 8,
      }}>
        <Heading ic={null} c={c}>
          <span style={{ fontSize: 17 }}>👥</span> Peer Comparison
        </Heading>
        <div style={{
          padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
          background: s.dark ? `${overallColor}18` : `${overallColor}10`,
          color: overallColor,
        }}>
          Ages {bracket.range} · Canada
        </div>
      </div>

      {/* Overall percentile hero */}
      <div style={{
        display: "flex", alignItems: "center", gap: 20,
        marginBottom: 24, flexWrap: "wrap",
      }}>
        {/* Big percentile number */}
        <div style={{
          width: 80, height: 80, borderRadius: 40,
          background: s.dark ? `${overallColor}12` : `${overallColor}08`,
          border: `2px solid ${overallColor}`,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <div style={{
            fontSize: 26, fontWeight: 800, color: overallColor,
            letterSpacing: "-0.04em", lineHeight: 1,
          }}>
            {overallPercentile}
          </div>
          <div style={{
            fontSize: 9, fontWeight: 700, color: overallColor,
            letterSpacing: "0.06em", textTransform: "uppercase",
            opacity: 0.8,
          }}>
            %ile
          </div>
        </div>

        {/* Message */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{
            fontSize: 14, fontWeight: 600, color: c.fg,
            lineHeight: 1.5, marginBottom: 4,
          }}>
            {overallPercentile >= 50
              ? `You're ahead of ${overallPercentile}% of your peers.`
              : `Building stronger than ${overallPercentile}% of your peers.`
            }
          </div>
          <div style={{
            fontSize: 12, color: c.muted, lineHeight: 1.5,
          }}>
            {message}
          </div>
        </div>
      </div>

      {/* Metric bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {comparisons.map((comp) => (
          <MetricBar key={comp.id} comp={comp} c={c} s={s} />
        ))}
      </div>

      {/* Source note */}
      <div style={{
        marginTop: 18, fontSize: 11, color: c.muted, opacity: 0.5,
        lineHeight: 1.4,
      }}>
        Based on Canadian financial benchmarks for university-educated adults.
        Percentiles are approximate.
      </div>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   METRIC BAR — individual comparison metric
   ═══════════════════════════════════════════════════ */

function MetricBar({ comp, c, s }) {
  const barColor = comp.percentile >= 75 ? c.green
    : comp.percentile >= 50 ? c.blue
    : comp.percentile >= 35 ? c.orange
    : c.red;

  return (
    <div>
      {/* Label row */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 6,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            fontSize: 13, fontWeight: 600, color: c.fg,
          }}>
            {comp.label}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 600, color: barColor,
          }}>
            {comp.value}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Above/below median badge */}
          <span style={{
            fontSize: 10, fontWeight: 700,
            color: comp.aboveMedian ? c.green : c.muted,
            letterSpacing: "0.04em",
          }}>
            {comp.aboveMedian ? "▲ Above" : "▽ Below"} median
          </span>
          <span style={{
            fontSize: 12, fontWeight: 700, color: barColor,
            fontVariantNumeric: "tabular-nums",
            minWidth: 32, textAlign: "right",
          }}>
            {comp.percentile}%
          </span>
        </div>
      </div>

      {/* Percentile bar */}
      <div style={{
        position: "relative", height: 6, borderRadius: 3,
        background: s.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}>
        {/* Fill */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: `${comp.percentile}%`,
          borderRadius: 3,
          background: barColor,
          transition: "width 0.8s cubic-bezier(0.25,0.1,0.25,1)",
        }} />
        {/* 50th percentile marker */}
        <div style={{
          position: "absolute", left: "50%", top: -1, bottom: -1,
          width: 1.5,
          background: s.dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
        }} />
      </div>

      {/* Median label */}
      <div style={{
        display: "flex", justifyContent: "center",
        marginTop: 3,
      }}>
        <span style={{
          fontSize: 10, color: c.muted, opacity: 0.5,
        }}>
          Median: {comp.median}
        </span>
      </div>
    </div>
  );
}
